import { WatchQueryOptions } from '@apollo/client';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
// Types
import {
  GetStream,
  GetStreamVariables,
  GetStream_stream,
} from 'api/streams/types/GetStream';
import { StreamStatus } from 'api/graphql-global-types';
// Helpers
import { getTalentProfileStreamsUrl, getEnvLink } from 'helpers/routes';
// Components
import Section from 'components/common3/Section/Section';
import StreamContentSections from './StreamContentSections/StreamContentSections';
import OrderStreamInfo from './OrderStreamInfo/OrderStreamInfo';
import StreamClipHero from './StreamClipHero/StreamClipHero';
import { HighlightClip } from './StreamHighlights/types';
// Styles
import styles from './OrderStream.module.scss';

type OrderStreamProps = {
  stream: GetStream_stream | undefined;
  updateStreamQuery: <TVars = GetStreamVariables>(
    mapFn: (
      previousQueryResult: GetStream,
      options: Pick<WatchQueryOptions<TVars, GetStream>, 'variables'>
    ) => GetStream
  ) => void;
  isFeaturedStream?: boolean;
};

const OrderStream = ({
  stream,
  updateStreamQuery,
  isFeaturedStream = false,
}: OrderStreamProps) => {
  const { push } = useRouter();
  const [playingClip, setPlayingClip] = useState<HighlightClip | null>(null);

  const isStreamHiddenOrCanceled =
    stream?.isHidden || stream?.streamStatus === StreamStatus.Cancelled;

  useEffect(() => {
    if (isStreamHiddenOrCanceled) {
      const redirectPath = getTalentProfileStreamsUrl(stream);
      push(redirectPath);
    }
  }, [isStreamHiddenOrCanceled, push, stream]);

  if (!stream || isStreamHiddenOrCanceled) {
    return null;
  }

  const { id: streamId } = stream;

  const interviewGuests =
    stream.isInterview && stream.interview
      ? {
          interviewer: stream.interview.interviewer,
          interviewee: stream.interview.interviewee,
        }
      : null;

  const hasParticipants = interviewGuests
    ? Boolean(interviewGuests.interviewer || interviewGuests.interviewee)
    : Boolean(stream.participants?.length);

  return (
    <Section title="" className={styles.sectionInfo}>
      {playingClip ? (
        // Clip takeover: the highlight plays in the hero in place of the
        // poster/buy block until the clip is closed.
        <StreamClipHero
          clip={playingClip}
          store={stream.store}
          shareUrlPath={`/${stream.store.slug}/streams/${stream.slug}?tab=highlights&clip=${playingClip.id}`}
          onClose={() => setPlayingClip(null)}
        />
      ) : (
        <OrderStreamInfo
          stream={stream}
          updateStreamQuery={updateStreamQuery}
          isFeaturedStream={isFeaturedStream}
        />
      )}

      <StreamContentSections
        streamId={streamId}
        streamName={stream.name || ''}
        streamUrl={`${getEnvLink()}/${stream.store.slug}/streams/${
          stream.slug
        }`}
        streamStartDate={stream.scheduleDateUtc}
        hasPrice={stream.price > 0}
        fanPollingEnabled={Boolean(stream.fanPollingEnabled)}
        sharePickGraphicsEnabled={Boolean(stream.sharePickGraphicsEnabled)}
        postEventContentHubEnabled={Boolean(stream.postEventContentHubEnabled)}
        isVotingOpen={stream.streamStatus === StreamStatus.Scheduled}
        streamSlug={stream.slug}
        storeId={stream.store.id}
        storeSlug={stream.store.slug || undefined}
        isStreamOver={
          stream.streamStatus === StreamStatus.Ended ||
          stream.streamStatus === StreamStatus.Interrupted
        }
        hasParticipants={hasParticipants}
        isInterview={stream.isInterview}
        interviewGuests={interviewGuests}
        onPlayHighlight={setPlayingClip}
        playingHighlightId={playingClip?.id ?? null}
      />
    </Section>
  );
};

export default OrderStream;
